/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Getting an agent's attention when Pearl is not what they are looking at.
 *
 * Three escalating reaches, because each one fails in a different place:
 *
 * - The ringtone works if the tab is allowed to make noise. It does nothing
 *   for an agent wearing no headphones, or muted, or in a meeting.
 * - A flashing document title works when Pearl is a background TAB. It is
 *   invisible when the browser itself is behind another application.
 * - A desktop notification is the only one that draws over another app, and
 *   the only one that can be clicked to bring the console forward.
 *
 * Permission is requested from the "go Available" click rather than on page
 * load. Asking on load is the pattern browsers now auto-deny, and an agent who
 * has just declared themselves ready to take calls is exactly the person for
 * whom the prompt makes sense.
 *
 * Everything degrades silently: a denied permission leaves the ringtone and
 * the title flash, which is still better than the previous behaviour.
 */

const TITLE_FLASH_MS = 1000;

let liveNotification = null;
let titleTimer = null;
let originalTitle = null;

function supported() {
  return typeof window !== "undefined" && "Notification" in window;
}

export function permission() {
  return supported() ? Notification.permission : "unsupported";
}

/**
 * Ask once, from inside a user gesture.
 *
 * Never re-prompts: once a user has denied, the browser will not ask again and
 * calling this repeatedly only produces a rejected promise every time.
 */
export function requestPermission() {
  if (!supported() || Notification.permission !== "default") return;
  try {
    const result = Notification.requestPermission();
    if (result && typeof result.catch === "function") result.catch(() => {});
  } catch {
    /* older Safari throws on the promise form; nothing to do */
  }
}

/**
 * Raise the incoming-call notification.
 *
 * `requireInteraction` keeps it on screen until it is dealt with. A
 * notification that auto-dismisses after a few seconds is one the agent misses
 * for exactly the same reason they missed the ring.
 */
export function showIncoming({ name, number, onClick }) {
  close();
  if (!supported() || Notification.permission !== "granted") return;

  try {
    liveNotification = new Notification(name || "Incoming call", {
      body: name ? `Incoming call from ${number || "an unknown number"}` : number || "",
      // A stable tag means a second call replaces the first rather than
      // stacking; the agent can only answer one anyway.
      tag: "pearl-incoming-call",
      requireInteraction: true,
      silent: true, // the ringer owns the audio; two sounds is worse than one
    });
    liveNotification.onclick = () => {
      try {
        window.focus();
      } catch {
        /* focus is best-effort */
      }
      if (onClick) onClick();
      close();
    };
  } catch {
    /* notification construction can throw on some platforms; ignore */
  }
}

export function close() {
  if (!liveNotification) return;
  try {
    liveNotification.close();
  } catch {
    /* already gone */
  }
  liveNotification = null;
}

/**
 * Alternate the document title so a background tab visibly blinks.
 *
 * setInterval is clamped in a background tab, but only to about once a second,
 * which is the rate we want anyway -- unlike the ringtone, this does not need
 * sub-second precision.
 */
export function startTitleFlash(label) {
  if (typeof document === "undefined" || titleTimer) return;
  originalTitle = document.title;
  let on = false;
  titleTimer = setInterval(() => {
    on = !on;
    document.title = on ? label : originalTitle;
  }, TITLE_FLASH_MS);
}

export function stopTitleFlash() {
  if (titleTimer) {
    clearInterval(titleTimer);
    titleTimer = null;
  }
  if (originalTitle !== null && typeof document !== "undefined") {
    document.title = originalTitle;
    originalTitle = null;
  }
}

/** Everything off, for when a call is answered, cancelled or missed. */
export function clearAll() {
  close();
  stopTitleFlash();
}
